/* eslint-disable react/no-danger -- rendering trusted formatted content from CMS */

import React, { useMemo } from 'react';
import {
  Badge,
  CardActionable,
  ContinuousLinearTracker,
  Heading,
} from '@americanexpress/dls-react';
import { useIntl } from 'react-intl';
import {
  array, arrayOf, bool, object, oneOfType,
} from 'prop-types';
import { IconSuccess } from '@americanexpress/dls-icons';
import { MembershipTextBadge } from '@americanexpress/web-react-extensions';
import { TrackerProgressLabels } from '@americanexpress/axp-benefits-utils/components';
import IconComponent from './IconComponent';
import styles from './AxpBenefitsActivity.scss';
import { mapMetaIcon } from '../utils/utils';

const UnifiedTracker = (props) => {
  const intl = useIntl();

  // Destructure trackers
  const [orderOneTracker = {}, orderTwoTracker = {}] = props?.trackers || [];
  const isCurrency = props?.isCurrency;

  // Memoized values for Order One Tracker
  const orderOneTrackerData = useMemo(() => {
    const detail = orderOneTracker?.metaContent?.content?.detail;

    return {
      isAchieved: orderOneTracker?.status === 'ACHIEVED',
      isInProgress: orderOneTracker?.status === 'IN_PROGRESS',
      isUnknown: orderOneTracker?.status === 'UNKNOWN',
      dlsIcon: mapMetaIcon(detail, orderOneTracker?.status, 1),
      progressStatusMessage: detail?.progress?.statusMsg,
      achievedStatusMessage: detail?.achieved?.statusMsg,
      sorBenefitId: orderOneTracker?.sorBenefitId,
      benefitName: orderOneTracker?.benefitName,
      trackerDuration: orderOneTracker?.trackerDuration,
      category: orderOneTracker?.category,
      totalSavingsYearToDate: orderOneTracker?.progress?.totalSavingsYearToDate,
    };
  }, [orderOneTracker]);

  // Memoized values for Order Two Tracker
  const orderTwoTrackerData = useMemo(() => {
    const progress = orderTwoTracker?.progress;
    const detail = orderTwoTracker?.metaContent?.content?.detail;

    return {
      tracker: orderTwoTracker?.tracker || {},
      progress,
      isAchieved: orderTwoTracker?.status === 'ACHIEVED',
      isInProgress: orderTwoTracker?.status === 'IN_PROGRESS',
      dlsIcon: mapMetaIcon(detail, orderTwoTracker?.status, 2),
      sorBenefitId: orderTwoTracker?.sorBenefitId,
      benefitName: orderTwoTracker?.benefitName,
      achievedStatusMessage: detail?.achieved?.statusMsg,
      progressStatusMessage: detail?.progress?.statusMsg,
      trackerDuration: orderTwoTracker?.trackerDuration,
      category: orderTwoTracker?.category,
      totalSavingsYearToDate: orderTwoTracker?.progress?.totalSavingsYearToDate,
    };
  }, [orderTwoTracker]);

  const title = orderTwoTracker?.progress?.title || orderOneTracker?.progress?.title;
  const targetCurrency = isCurrency ? orderTwoTracker?.tracker?.targetCurrency : 'USD';
  const spentAmount = orderTwoTrackerData?.tracker?.spentAmount;
  const remainingAmount = orderTwoTrackerData?.tracker?.remainingAmount;

  const renderInfoPill = () => {
    if (!orderOneTrackerData?.totalSavingsYearToDate
            || !orderOneTrackerData?.trackerDuration
            || orderOneTrackerData?.trackerDuration === 'CalenderYear'
            || orderOneTrackerData?.category !== 'usage') return null;
    return (
      <MembershipTextBadge
        className="pad-1-tb row margin-0"
        size="small"
        status="information"
        data-testid={`info-pill-${title}`}
      >
        {intl.formatMessage({ id: 'earned-this-year', defaultMessage: 'Earned this Year' })}: ${orderOneTrackerData?.totalSavingsYearToDate}
      </MembershipTextBadge>
    );
  };

  const shouldRenderMetaBadgeForProgress = orderOneTrackerData?.isInProgress
      || (orderOneTrackerData?.isUnknown && !orderTwoTrackerData?.isAchieved);

  const isCompletedState = !orderOneTrackerData?.isInProgress && orderTwoTrackerData?.isAchieved;

  const renderTrackers = () => (
    <>
      {!isCompletedState && (
      <div id={`progress-section-${orderTwoTrackerData?.benefitName}`} data-testid={`progress-section-${orderTwoTrackerData.benefitName}`} className="flex flex-column">
        {orderOneTrackerData?.progressStatusMessage && shouldRenderMetaBadgeForProgress && (
          <MembershipTextBadge
            className="flex pad-1-tb"
            icon={(
              <IconComponent
                icon={orderOneTrackerData?.dlsIcon}
                isMetaContent={true}
                iconColor={`${orderOneTrackerData?.isUnknown ? 'neutral' : 'white'}`}
                isFilled={true}
              />
            )}
            status={`${orderOneTrackerData?.isUnknown ? 'neutral' : 'informationAlt'}`}
            size="small"
            type="text"
          >
            <span className="body-xsmall-bold" dangerouslySetInnerHTML={{ __html: orderOneTrackerData?.progressStatusMessage }} />
          </MembershipTextBadge>
        )}
        {orderOneTrackerData?.achievedStatusMessage
          && orderOneTrackerData?.isAchieved
          && !orderTwoTrackerData?.isAchieved
          && (
            <MembershipTextBadge
              className="flex pad-1-tb"
              icon={(
                <IconComponent
                  icon={orderOneTrackerData?.dlsIcon}
                  isMetaContent={true}
                />
              )}
              status="success"
              size="small"
              type="text"
            >
              <span className="body-xsmall-bold" dangerouslySetInnerHTML={{ __html: orderOneTrackerData?.achievedStatusMessage }} />
            </MembershipTextBadge>
          )}
        {orderTwoTrackerData?.isInProgress && (
          <>
            <div className="flex flex-row">
              <div className="flex flex-column flex-item-grow margin-2-t stack-1">
                <ContinuousLinearTracker
                  id={`linear-tracker-${orderTwoTrackerData?.benefitName}`}
                  max={Number(orderTwoTrackerData?.tracker?.targetAmount)}
                  min={0}
                  value={Number(spentAmount)}
                  color="brand"
                  hideTrackerEndText={true}
                  hideTrackerStartText={true}
                  data-testid={`linear-tracker-${orderTwoTrackerData?.benefitName}`}
                  hasGradient={true}
                />
                <TrackerProgressLabels
                  id={`tracker-labels-${orderTwoTrackerData?.benefitName}`}
                  spentAmount={spentAmount}
                  remainingAmount={remainingAmount}
                  usedLabel={orderTwoTrackerData?.progress?.usedLabel}
                  togoLabel={orderTwoTrackerData?.progress?.togoLabel}
                  targetCurrency={targetCurrency}
                  isCurrency={isCurrency}
                  leftLabelClassNames="body-small-bold pull-left-xs-up"
                  rightLabelClassNames="body-small-bold pull-right-xs-up"
                  spentAmountClassNames="color-text-brand"
                />
              </div>
              <div className="pad-1-l">
                <IconComponent
                  icon={orderTwoTrackerData?.dlsIcon}
                  hideBackground={true}
                  isFilled={true}
                />
              </div>
            </div>
            <div
              id={`description-${orderTwoTrackerData?.benefitName}`}
              data-testid={`description-${orderTwoTrackerData?.benefitName}`}
              className="flex text-align-left margin-responsive-tb"
            >
              <span className="body" dangerouslySetInnerHTML={{ __html: orderTwoTrackerData?.progressStatusMessage }} />
            </div>
          </>
        )}
        {orderOneTrackerData?.isInProgress && orderTwoTrackerData?.isAchieved && (
          <Badge
            className="pad-1-tb"
            icon={<IconComponent icon={orderTwoTrackerData?.dlsIcon} isMetaContent={true} />}
            status="success"
            size="small"
            type="text"
          >
            <span className="body-xsmall-bold pad-1-tb" dangerouslySetInnerHTML={{ __html: orderTwoTrackerData?.achievedStatusMessage }} />
          </Badge>
        )}
      </div>
      )}
      {isCompletedState && (
        <div className="flex flex-row flex-align-center flex-item-grow">
          <div className="flex pad-0-l flex-column flex-align-start flex-item-grow">
            <Heading
              id={`heading-${title}`}
              data-testid={`heading-${title}`}
              level={3}
              variant="sans-small-bold"
              className="color-text-regular text-align-left margin-0"
            >
              {title}
            </Heading>
            {orderOneTrackerData.achievedStatusMessage && (
            <MembershipTextBadge
              className="flex pad-1-tb"
              icon={(
                <IconComponent
                  icon={orderOneTrackerData?.dlsIcon}
                  isMetaContent={true}
                  iconColor={`${orderOneTrackerData?.isUnknown ? 'neutral' : 'success'}`}
                  isFilled={false}
                />
              )}
              status={`${orderOneTrackerData?.isUnknown ? 'neutral' : 'success'}`}
              size="small"
              type="text"
            >
              <span className="body-xsmall-bold" dangerouslySetInnerHTML={{ __html: orderOneTrackerData?.achievedStatusMessage }} />
            </MembershipTextBadge>
            )}
            {orderTwoTrackerData?.achievedStatusMessage && orderTwoTrackerData.category === 'spend' && orderOneTrackerData.category === 'usage' ? (
              <MembershipTextBadge
                className="flex pad-1-tb"
                icon={(
                  <IconComponent
                    icon={orderTwoTrackerData?.dlsIcon}
                    isMetaContent={true}
                  />
                )}
                status={`${orderOneTrackerData?.isUnknown ? 'neutral' : 'success'}`}
                size="small"
                type="text"
              >
                <span className="body-xsmall-bold" dangerouslySetInnerHTML={{ __html: orderTwoTrackerData?.achievedStatusMessage }} />
              </MembershipTextBadge>
            ) : (
              <span
                className="body pad-1-tb text-align-left"
                data-testid={`desc-${orderTwoTrackerData?.benefitName}`}
                dangerouslySetInnerHTML={{ __html: orderTwoTrackerData?.achievedStatusMessage }}
              />
            )}
            { renderInfoPill() }
          </div>
          <div className="flex flex-column flex-align-center pad-3-l">
            <div
              data-testid={`icon-success-wrapper-${orderTwoTrackerData?.benefitName}`}
              className={styles.iconSuccessBorderRing}
            >
              <IconSuccess
                size="xl"
                color="success"
                titleId={`icon-success-${orderTwoTrackerData?.benefitName}`}
                data-testid={`icon-success-${orderTwoTrackerData?.benefitName}`}
                isFilled={true}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <CardActionable
      id={`container-${title}`}
      data-testid={`container-${title}`}
      className="width-full height-full"
      innerContainerClassName="pad-3 flex-item-grow"
    >
      {!isCompletedState && (
        <Heading
          id={`heading-${title}`}
          data-testid={`heading-${title}`}
          level={3}
          variant="sans-small-bold"
          className="color-text-regular text-align-left margin-0"
        >
          {title}
        </Heading>
      )}
      {renderTrackers()}
    </CardActionable>
  );
};

UnifiedTracker.propTypes = {
  trackers: arrayOf(oneOfType([array, object])).isRequired,
  isCurrency: bool.isRequired,
};

export default UnifiedTracker;
/* eslint-enable react/no-danger */
